import { ArrowDropDown } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Typography,
} from "@mui/material";
import { DataTable, ValueFn } from "@platform/design-system";

import {
  MARKETING_PRESENTATION_REASON_LABELS,
  REQUOTE_REASONS,
} from "../constants";
import { Proposal as ProposalType, Quote } from "../types";
import { getProductDisplayName } from "../utils";

type Proposal = {
  errors?: unknown[];
} & Pick<ProposalType, "submittedDate" | "quotes" | "current" | "next"> &
  Partial<Pick<ProposalType, "receivedDate">>;

type Props<T> = {
  proposal: T;
  index: number;
  onSelectQuote: (quote: Quote) => void;
  onViewError?: (proposal: T) => void;
  onEdit?: (quote: Quote) => void;
  defaultExpanded: boolean;
  renderHeader?: (args: { proposal: Proposal; num: number }) => React.ReactNode;
};

function CollapsableProposal<T extends Proposal>({
  proposal,
  onSelectQuote,
  defaultExpanded,
  index,
  renderHeader,
  onViewError,
  onEdit,
}: Props<T>) {
  const num = index + 1;
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      sx={{
        boxShadow: "none",
        border: 1,
        borderColor: "neutrals.neutral_300",
        borderRadius: `8px !important`,
      }}
    >
      <AccordionSummary
        expandIcon={<ArrowDropDown />}
        sx={{
          bgcolor: "neutrals.neutral_200",
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          flexDirection: "row-reverse",
          gap: 1.25,
        }}
      >
        {renderHeader ? (
          renderHeader({ proposal, num })
        ) : (
          <Typography component="span">{`Proposal #${num}`}</Typography>
        )}
      </AccordionSummary>
      {proposal.errors && proposal.errors.length > 0 ? (
        <Alert
          severity="error"
          sx={{ borderRadius: 0 }}
          action={
            <Button
              variant="text"
              sx={{ color: "inherit" }}
              onClick={() => onViewError?.(proposal)}
            >
              View Details
            </Button>
          }
        >
          Carrier rejected this RFP submission.
        </Alert>
      ) : null}
      <AccordionDetails sx={{ padding: 3 }}>
        <Grid container mb={3}>
          <GridInfo title="RFP Sent" text={proposal.submittedDate ?? "-"} />
          <GridInfo
            title="Proposal Received"
            text={proposal.receivedDate ?? "-"}
          />
        </Grid>
        <DataTable
          configTable={[
            {
              field: "productType",
              headerName: "Coverage",
              width: "290px",
              cellConfig: {
                type: "textWithSubtext",
                inline: true,
                highlightConditionField: "proposal.receivedDate",
                formatter: (_, row: Quote) =>
                  getProductDisplayName(row.productType, row.productPlanType),
                subtextField: "isUnsolicited",
                subtextCondition: (value, _) => Boolean(value),
                subtextLabel: "Additional Offer",
                subtextFontStyle: "italic",
              },
            },
            {
              field: "currentStatus.text",
              headerName: "Status",
              width: "250px",
              cellConfig: {
                type: "textWithSubtext" as const,
                subtextField: "currentStatus.otherReason",
                subtextFormatter: (value: ValueFn, row: Quote) => {
                  const exclusionReason = row.currentStatus?.exclusionReason
                    ? (MARKETING_PRESENTATION_REASON_LABELS[
                        row.currentStatus
                          .exclusionReason as keyof typeof MARKETING_PRESENTATION_REASON_LABELS
                      ] ?? row.currentStatus.exclusionReason)
                    : "";
                  return `${value || exclusionReason || ""}`;
                },
              },
            },
            {
              field: "requoteRequired",
              headerName: "Requote Needed",
              width: "260px",
              cellConfig: {
                type: "textWithSubtext",
                subtextField: "requoteReason",
                formatter: (value) => parseYesNo(value as boolean),
                subtextFormatter: (value) =>
                  value
                    ? (REQUOTE_REASONS?.[
                        value as keyof typeof REQUOTE_REASONS
                      ] ?? value.toString())
                    : "",
              },
            },
            ...(onEdit
              ? [
                  {
                    field: "action",
                    headerName: "",
                    width: "50px",
                    cellConfig: {
                      type: "action" as const,
                      onClick: (row: Quote) => {
                        onEdit?.(row);
                      },
                    },
                  },
                ]
              : []),
          ]}
          data={proposal.quotes}
          onRowClick={onSelectQuote}
          stripRows
        />
      </AccordionDetails>
    </Accordion>
  );
}

const GridInfo = ({ title, text }: { title: string; text: string }) => (
  <Grid size={{ xs: 12, md: 3 }}>
    <Typography variant="body1" fontWeight="600">
      {title}
    </Typography>
    <Typography variant="body1">{text}</Typography>
  </Grid>
);

const parseYesNo = (value: boolean | null) => (value ? "Yes" : "");

export default CollapsableProposal;
